// Shopify Storefront API client for Farveladeland
// Real catalog: 1,749 products, 56 collections — mirrored from farveladeland.com

const SHOPIFY_API_VERSION = "2025-07";
const SHOPIFY_STORE_PERMANENT_DOMAIN = "farveladeland.myshopify.com";
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
// Storefront API token. This is a public, read-only token by design (Shopify
// Storefront API), but we read it from VITE_SHOPIFY_STOREFRONT_TOKEN so it can
// be rotated via env config rather than a code change. Falls back to the
// existing token to avoid breaking the live site if the env var is unset.
const SHOPIFY_STOREFRONT_TOKEN =
  (import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN as string | undefined) ||
  "5542cad865792a96df867c530b29549c";

export { SHOPIFY_STORE_PERMANENT_DOMAIN };

export interface Money {
  amount: string;
  currencyCode: string;
}

export interface ShopifyImage {
  url: string;
  altText: string | null;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: Money;
  compareAtPrice?: Money | null;
  availableForSale: boolean;
  quantityAvailable?: number | null;
  selectedOptions: Array<{ name: string; value: string }>;
}

export interface ShopifyMetaobjectField {
  key: string;
  value: string | null;
}

export interface ShopifyMetafield {
  value: string | null;
  references?: {
    nodes: Array<{ fields?: ShopifyMetaobjectField[] }>;
  } | null;
}

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    availableForSale: boolean;
    publishedAt?: string | null;
    createdAt?: string | null;
    priceRange: { minVariantPrice: Money };
    images: { edges: Array<{ node: ShopifyImage }> };
    variants: { edges: Array<{ node: ShopifyVariant }> };
    options: Array<{ name: string; values: string[] }>;
    colorMetafield?: ShopifyMetafield | null;
  };
}

export interface ShopifyCollection {
  id: string;
  handle: string;
  title: string;
  description: string;
  image: ShopifyImage | null;
}

export async function storefrontApiRequest<T = unknown>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<{ data?: T; errors?: Array<{ message: string }> } | null> {
  // Pull the operation name (e.g. "ProductByHandle") from the query so log
  // lines are actionable when triaging intermittent Storefront failures.
  const opName = query.match(/\b(query|mutation)\s+(\w+)/)?.[2] ?? "anon";
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (response.status === 402) {
      console.error(`[shopify:${opName}] billing required (402)`);
      return null;
    }
    if (!response.ok) {
      console.error(`[shopify:${opName}] HTTP ${response.status}`, { variables });
      return null;
    }
    const data = await response.json();
    if (data.errors) {
      console.error(`[shopify:${opName}] graphql errors`, data.errors);
    }
    return data;
  } catch (err) {
    console.error(`[shopify:${opName}] request failed`, err);
    return null;
  }
}

// ----- Queries -----

export const COLLECTION_LIST_QUERY = `
  query Collections($first: Int!) {
    collections(first: $first, sortKey: TITLE) {
      edges {
        node {
          id
          handle
          title
          description
          image { url altText }
        }
      }
    }
  }
`;

export const COLLECTION_BY_HANDLE_QUERY = `
  query CollectionByHandle($handle: String!, $first: Int!) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      image { url altText }
      products(first: $first) {
        edges {
          node {
            id
            title
            description
            handle
            availableForSale
            publishedAt
            createdAt
            priceRange { minVariantPrice { amount currencyCode } }
            images(first: 2) { edges { node { url altText } } }
            variants(first: 50) {
              edges {
                node {
                  id
                  title
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  availableForSale
                  selectedOptions { name value }
                }
              }
            }
            options { name values }
          }
        }
      }
    }
  }
`;

export const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $after: String, $query: String, $sortKey: ProductSortKeys, $reverse: Boolean) {
    products(first: $first, after: $after, query: $query, sortKey: $sortKey, reverse: $reverse) {
      pageInfo { hasNextPage endCursor }
      edges {
        cursor
        node {
          id
          title
          description
          handle
          availableForSale
          publishedAt
          createdAt
          priceRange { minVariantPrice { amount currencyCode } }
          images(first: 2) { edges { node { url altText } } }
          variants(first: 50) {
            edges {
              node {
                id
                title
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                availableForSale
                selectedOptions { name value }
              }
            }
          }
          options { name values }
          colorMetafield: metafield(namespace: "shopify", key: "color-pattern") {
            value
            references(first: 10) {
              nodes {
                ... on Metaobject {
                  fields { key value }
                }
              }
            }
          }
        }
      }
    }
  }
`;


export const PRODUCT_BY_HANDLE_QUERY = `
  query ProductByHandle($handle: String!) {
    product(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      productType
      availableForSale
      priceRange { minVariantPrice { amount currencyCode } }
      images(first: 20) { edges { node { url altText } } }
      variants(first: 25) {
        edges {
          node {
            id
            title
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            availableForSale
            quantityAvailable
            selectedOptions { name value }
          }
        }
      }
      options { name values }
    }
  }
`;

// ----- Helpers -----

export function formatMoney(money: Money | undefined | null): string {
  if (!money) return "";
  const amount = parseFloat(money.amount);
  if (money.currencyCode === "DKK") {
    return `${amount.toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} DKK`;
  }
  return `${amount.toFixed(2)} ${money.currencyCode}`;
}

/**
 * Compute the price to display on product cards. Shopify's
 * `priceRange.minVariantPrice` includes ALL variants — even sold-out or
 * archived ones — which can leave a card showing a price that doesn't
 * actually correspond to anything a customer can buy. To match what's
 * really on the shelf, we take the minimum across `availableForSale`
 * variants. Falls back to all variants if none are available, then to
 * `minVariantPrice` if the card was loaded without variant data.
 *
 * Returns `{ price, isRange }` so callers can decide whether to prefix
 * the label with "Fra".
 */
export function cardPriceFromProduct(
  node: ShopifyProduct["node"],
): { price: Money; compareAt: Money | null; isRange: boolean; isOnSale: boolean } {
  const variants = node.variants?.edges?.map((e) => e.node) ?? [];
  const available = variants.filter((v) => v.availableForSale);
  const pool = available.length > 0 ? available : variants;
  if (pool.length === 0) {
    return { price: node.priceRange.minVariantPrice, compareAt: null, isRange: false, isOnSale: false };
  }
  let minVariant = pool[0];
  let maxAmount = parseFloat(pool[0].price.amount);
  for (const v of pool) {
    const a = parseFloat(v.price.amount);
    if (a < parseFloat(minVariant.price.amount)) minVariant = v;
    if (a > maxAmount) maxAmount = a;
  }
  const compareAt = minVariant.compareAtPrice ?? null;
  const isOnSale =
    !!compareAt && parseFloat(compareAt.amount) > parseFloat(minVariant.price.amount);
  return {
    price: minVariant.price,
    compareAt: isOnSale ? compareAt : null,
    isRange: parseFloat(minVariant.price.amount) !== maxAmount,
    isOnSale,
  };
}

export async function fetchCollections(): Promise<ShopifyCollection[]> {
  const res = await storefrontApiRequest<{
    collections: { edges: Array<{ node: ShopifyCollection }> };
  }>(COLLECTION_LIST_QUERY, { first: 100 });
  return res?.data?.collections?.edges?.map((e) => e.node) ?? [];
}

export type CollectionWithProducts = ShopifyCollection & {
  products: { edges: ShopifyProduct[] };
};

export async function fetchCollection(
  handle: string,
  first = 48
): Promise<CollectionWithProducts | null> {
  const res = await storefrontApiRequest<{ collection: CollectionWithProducts | null }>(
    COLLECTION_BY_HANDLE_QUERY,
    { handle, first }
  );
  return res?.data?.collection ?? null;
}

export async function fetchProducts(opts: {
  first?: number;
  query?: string;
  sortKey?: string;
  reverse?: boolean;
} = {}): Promise<ShopifyProduct[]> {
  const res = await storefrontApiRequest<{
    products: { edges: ShopifyProduct[] };
  }>(PRODUCTS_QUERY, {
    first: opts.first ?? 12,
    query: opts.query,
    sortKey: opts.sortKey ?? "CREATED_AT",
    reverse: opts.reverse ?? true,
  });
  return res?.data?.products?.edges ?? [];
}

/**
 * Page through Storefront `products` until we hit `max` or exhaust results.
 * Used by /storrelse where we need to scan the catalog wider than 250.
 */
export async function fetchAllProducts(opts: {
  query?: string;
  sortKey?: string;
  reverse?: boolean;
  max?: number;
  pageSize?: number;
} = {}): Promise<ShopifyProduct[]> {
  const max = opts.max ?? 1000;
  const pageSize = Math.min(opts.pageSize ?? 250, 250);
  const out: ShopifyProduct[] = [];
  let after: string | undefined = undefined;
  for (let i = 0; i < 10 && out.length < max; i++) {
    const res: any = await storefrontApiRequest<{
      products: { edges: ShopifyProduct[]; pageInfo: { hasNextPage: boolean; endCursor: string | null } };
    }>(PRODUCTS_QUERY, {
      first: Math.min(pageSize, max - out.length),
      after,
      query: opts.query,
      sortKey: opts.sortKey ?? "CREATED_AT",
      reverse: opts.reverse ?? true,
    });
    const edges = res?.data?.products?.edges ?? [];
    out.push(...edges);
    const info = res?.data?.products?.pageInfo;
    if (!info?.hasNextPage || !info?.endCursor) break;
    after = info.endCursor;
  }
  return out;
}


export type ProductDetail = ShopifyProduct["node"] & { descriptionHtml?: string; productType?: string };

export async function fetchProduct(handle: string): Promise<ProductDetail | null> {
  const res = await storefrontApiRequest<{ product: ProductDetail | null }>(
    PRODUCT_BY_HANDLE_QUERY,
    { handle }
  );
  return res?.data?.product ?? null;
}

// ----- Predictive search (type-ahead) -----
//
// Storefront's `predictiveSearch` endpoint is purpose-built for type-ahead:
// it returns lightweight product hits (image, title, price, handle) plus
// query suggestions, ranked by relevance. We keep the response shape small
// so the search panel stays snappy.

export const PREDICTIVE_SEARCH_QUERY = `
  query Predictive($q: String!, $limit: Int!) {
    predictiveSearch(query: $q, limit: $limit, types: [PRODUCT, QUERY]) {
      products {
        id
        title
        handle
        availableForSale
        priceRange { minVariantPrice { amount currencyCode } }
        featuredImage { url altText }
      }
      queries { text }
    }
  }
`;

export type PredictiveProduct = {
  id: string;
  title: string;
  handle: string;
  availableForSale: boolean;
  priceRange: { minVariantPrice: Money };
  featuredImage: ShopifyImage | null;
};

export type PredictiveResult = {
  products: PredictiveProduct[];
  queries: Array<{ text: string }>;
};

export async function fetchPredictive(
  q: string,
  limit = 6
): Promise<PredictiveResult> {
  if (!q.trim()) return { products: [], queries: [] };
  const res = await storefrontApiRequest<{ predictiveSearch: PredictiveResult }>(
    PREDICTIVE_SEARCH_QUERY,
    { q, limit }
  );
  return (
    res?.data?.predictiveSearch ?? { products: [], queries: [] }
  );
}

